import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, themeColor, Flex, Copy, Button, Scale, themeMediaQuery } from '@elparking/components'
import { FormattedMessage } from 'react-intl'
import SingleOptionsFilter from './sections/SingleOptionsFilter'
import MultiOptionsFilter from './sections/MultiOptionsFilter'
import {
  RENTAL_CAR_VEHICLE_FUEL_TYPE_GASOLINE,
  RENTAL_CAR_VEHICLE_FUEL_TYPE_DIESEL,
  RENTAL_CAR_VEHICLE_FUEL_TYPE_ELECTRIC,
  RENTAL_CAR_VEHICLE_FUEL_TYPE_HYBRID,
  RENTAL_CAR_VEHICLE_GEAR_BOX_MANUAL,
  RENTAL_CAR_VEHICLE_GEAR_BOX_AUTOMATIC,
} from 'commons/js/constants'

const FiltersBoxContainer = styled(Flex)`
    ${themeMediaQuery('medium')} {
      padding: 20px;
      border: 1px solid ${themeColor('gray10')};
    }
`

export const defaultFuelTypeFilter = [
  { label: <FormattedMessage defaultMessage='Gasolina' />, value: RENTAL_CAR_VEHICLE_FUEL_TYPE_GASOLINE },
  { label: <FormattedMessage defaultMessage='Diésel' />, value: RENTAL_CAR_VEHICLE_FUEL_TYPE_DIESEL },
  { label: <FormattedMessage defaultMessage='Eléctrico' />, value: RENTAL_CAR_VEHICLE_FUEL_TYPE_ELECTRIC },
  { label: <FormattedMessage defaultMessage='Híbrido' />, value: RENTAL_CAR_VEHICLE_FUEL_TYPE_HYBRID },
]

export const defaultGearBoxFilter = [
  { label: <FormattedMessage defaultMessage='Manual' />, value: RENTAL_CAR_VEHICLE_GEAR_BOX_MANUAL },
  { label: <FormattedMessage defaultMessage='Automático' />, value: RENTAL_CAR_VEHICLE_GEAR_BOX_AUTOMATIC },
]

const defaultPassengersFilter = [
  { label: '2', value: '2' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7+', value: '7' },
]

const defaultBaggageFilter = [
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4+', value: '4' },
]

const defaultDistanceExtraKmFilter = [
  { label: '0', value: 0 },
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '15', value: 15 },
  { label: '20+', value: 20 },
]

const getDefaultDistanceExtraKmFilter = (kmRange) => kmRange.map((val) => ({ label: val, value: val }))

const FiltersBox = ({
  filters,
  setFilters,
  cleanFilters,
  isLoading,
  isModal,
  filtersConfig,
}) => {
  const [fuelTypeFilter, setFuelTypeFilter] = useState((filters && filters.fuelType))
  const [gearBoxFilter, setGearBoxFilter] = useState((filters && filters.gearBox))
  const [passengersFilter, setPassengersFilter] = useState((filters && filters.passengers))
  const [baggageFilter, setBaggageFilter] = useState((filters && filters.baggage))
  const [distanceExtraKmFilter, setDistanceExtraKmFilter] = useState((filters && filters.distanceExtraKm))
  const distanceExtraKmFilterValues = filtersConfig && filtersConfig.kmRange && getDefaultDistanceExtraKmFilter(filtersConfig.kmRange)
  useEffect(() => {
    setFilters({
      ...filters,
      fuelType: fuelTypeFilter,
      gearBox: gearBoxFilter,
      passengers: passengersFilter,
      baggage: baggageFilter,
      distanceExtraKm: distanceExtraKmFilter,
    }
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuelTypeFilter, gearBoxFilter, passengersFilter, baggageFilter, distanceExtraKmFilter])
  return (
      <FiltersBoxContainer flexDirection='column'>
          <Flex marginBottom='1em' alignItems='center' justifyContent='space-between'>
              <Copy as='h2' padding='0' fontSize='large' fontColor='accent_dark'><FormattedMessage defaultMessage='Filtros' /></Copy>
              <Button.unstyled onClick={cleanFilters} textTransform='capitalize' fontSize='xSmall' disabled={isLoading}><FormattedMessage defaultMessage='Borrar filtros' /></Button.unstyled>
          </Flex>
          <MultiOptionsFilter options={defaultFuelTypeFilter} disabled={isLoading} title={<FormattedMessage defaultMessage='Tipo de combustible' />} keyId={`fuelType-${isModal}-`} filter={fuelTypeFilter} setFilters={setFuelTypeFilter} />
          <SingleOptionsFilter options={defaultGearBoxFilter} disabled={isLoading} title={<FormattedMessage defaultMessage='Tipo de cambio' />} keyId={`gear-${isModal}-`} filter={gearBoxFilter} setFilters={setGearBoxFilter} />
          <SingleOptionsFilter options={defaultPassengersFilter} isHorizontal disabled={isLoading} title={<FormattedMessage defaultMessage='Número de asientos' />} keyId={`passengers-${isModal}-`} filter={passengersFilter} setFilters={setPassengersFilter} />
          <SingleOptionsFilter options={defaultBaggageFilter} isHorizontal disabled={isLoading} title={<FormattedMessage defaultMessage='Número de maletas' />} keyId={`baggage-${isModal}-`} filter={baggageFilter} setFilters={setBaggageFilter} />
          <Flex flexDirection='column'>
              <Copy padding='0' fontSize='small' fontColor='gray' ><FormattedMessage defaultMessage='Recogida del vehículo (km)' /></Copy>
              <Copy fontColor='gray90' padding='0.6em 0 0 0' fontSize='xSmall' fontWeight='regular' ><FormattedMessage defaultMessage='Distancia máxima a la que mostraremos los resultados de búsqueda.' /></Copy>
              <Box width='100%' marginTop='1em'>
                  <Scale
                    dataTest='distance-extra-km-filter-scale'
                    fontSize='small'
                    fontWeight='regular'
                    fontColor='black'
                    id='scale-item'
                    value={distanceExtraKmFilter}
                    disabled={isLoading}
                    onChange={setDistanceExtraKmFilter}
                    options={distanceExtraKmFilterValues || defaultDistanceExtraKmFilter}
          />
              </Box>
          </Flex>
      </FiltersBoxContainer>
  )
}

FiltersBox.propTypes = {
  filters: PropTypes.shape({
    fuelType: PropTypes.any,
    gearBox: PropTypes.string,
    passengers: PropTypes.string,
    baggage: PropTypes.string,
    distanceExtraKm: PropTypes.any,
  }),
  setFilters: PropTypes.func,
  cleanFilters: PropTypes.func,
  isLoading: PropTypes.bool,
  isModal: PropTypes.bool,
  filtersConfig: PropTypes.object,
}

FiltersBox.defaultProps = {
  isLoading: false,
  isModal: false,
}

export default FiltersBox
